import { useMemo, useState } from 'react';
import { store, useAppStore } from '../store/appStore';
import {
  dirtyKeys,
  filamentKeysOf,
  findFilamentPreset,
  findPrinterPreset,
  findProcessPreset,
  loadUserPresets,
  processKeysOf,
} from '../settings/presets';
import { openSettingsPanel } from '../ui/layout';

const INFILL_CHOICES = [0, 10, 15, 20, 25, 30, 40, 50, 100];

function shortPresetName(name: string) {
  return name.replace(/\s*@BBL P1S\s*$/, '').replace(/ nozzle$/, '');
}

function colourOf(settings: Record<string, string | number | boolean>) {
  const raw = String(settings.filament_colour ?? '#E8C547');
  return raw.startsWith('#') ? raw : `#${raw}`;
}

export function PresetStrip() {
  const settings = useAppStore((s) => s.settings);
  const processPresetId = useAppStore((s) => s.processPresetId);
  const filamentPresetId = useAppStore((s) => s.filamentPresetId);
  const printerPresetId = useAppStore((s) => s.printerPresetId);
  const [infillOpen, setInfillOpen] = useState(false);

  const users = useMemo(() => loadUserPresets(), [processPresetId, filamentPresetId]);
  const processPreset = findProcessPreset(processPresetId, users);
  const filamentPreset = findFilamentPreset(filamentPresetId, users);
  const printerPreset = findPrinterPreset(printerPresetId);

  const procDirty = useMemo(
    () => dirtyKeys(settings, processPreset, processPreset ? processKeysOf(processPreset) : []),
    [settings, processPreset]
  );
  const filDirty = useMemo(
    () => dirtyKeys(settings, filamentPreset, filamentPreset ? filamentKeysOf(filamentPreset) : []),
    [settings, filamentPreset]
  );

  const infill = Number(settings.sparse_infill_density ?? 15);
  const supportOn = Boolean(settings.enable_support);
  const adhesionOn = String(settings.brim_type ?? 'auto_brim') !== 'no_brim';

  function openPanel() {
    openSettingsPanel();
  }

  return (
    <div className="preset-strip" data-testid="preset-strip">
      <button type="button" className="strip-seg" onClick={openPanel} title="Printer preset">
        <span className="seg-kicker">Printer</span>
        <span className="seg-value">{printerPreset?.name || 'Bambu Lab P1S 0.4 nozzle'}</span>
      </button>
      <button type="button" className="strip-seg" onClick={openPanel} title="Filament preset">
        <span className="swatch" style={{ background: colourOf(settings) }} />
        <span className="seg-copy">
          <span className="seg-kicker">Filament</span>
          <span className={`seg-value ${filDirty.length ? 'modified' : ''}`}>
            {filamentPreset?.name || 'Custom'}
            {filDirty.length ? ' *' : ''}
          </span>
        </span>
      </button>
      <div className="strip-seg process-seg">
        <button type="button" className="seg-main" onClick={openPanel} title="Process preset">
          <span className="seg-kicker">Process</span>
          <span className={`seg-value ${procDirty.length ? 'modified' : ''}`}>
            {shortPresetName(processPreset?.name || 'Custom')}
            {procDirty.length ? ' *' : ''}
          </span>
        </button>
        <div className="quick-chips">
          <div className="chip-wrap">
            <button type="button" className="chip" onClick={() => setInfillOpen((v) => !v)}>
              {Math.round(infill)}%
            </button>
            {infillOpen && (
              <div className="chip-menu">
                {INFILL_CHOICES.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={n === infill ? 'active' : ''}
                    onClick={() => {
                      store.setSetting('sparse_infill_density', n);
                      setInfillOpen(false);
                    }}
                  >
                    {n}%
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className={`chip ${supportOn ? 'on' : ''}`}
            onClick={() => store.setSetting('enable_support', !supportOn)}
          >
            Support {supportOn ? 'On' : 'Off'}
          </button>
          <button
            type="button"
            className={`chip ${adhesionOn ? 'on' : ''}`}
            onClick={() => store.setSetting('brim_type', adhesionOn ? 'no_brim' : 'auto_brim')}
          >
            Adhesion {adhesionOn ? 'On' : 'Off'}
          </button>
        </div>
      </div>
    </div>
  );
}
